// Cliente HTTP minimo. fetch nativo + cookies same-origin.
// En dev Vite hace proxy /api -> :8000 (vite.config.ts).

export class ApiError extends Error {
  constructor(public status: number, public detail: string) {
    super(detail);
  }
}


/**
 * Normaliza el campo `detail` de una respuesta de error. FastAPI usa un
 * string para HTTPException, pero una LISTA de objetos {loc,msg,type} para
 * errores de validacion (422). Si esa lista llegara cruda a un componente
 * React reventaria el render ("Objects are not valid as a React child") y
 * la pantalla se queda en blanco. Aqui la aplanamos a texto legible.
 */
function normalizeDetail(detail: unknown): string | undefined {
  if (detail == null) return undefined;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    const partes = detail.map((e) => {
      if (e && typeof e === "object" && "msg" in e) {
        const loc = Array.isArray((e as { loc?: unknown }).loc)
          ? ((e as { loc: unknown[] }).loc)
          : [];
        const campo = loc.length ? String(loc[loc.length - 1]) : "";
        const msg = String((e as { msg: unknown }).msg);
        return campo ? `${campo}: ${msg}` : msg;
      }
      return typeof e === "string" ? e : JSON.stringify(e);
    });
    return partes.join(" \u00b7 ");
  }
  return JSON.stringify(detail);
}

async function request<T>(
  method: string,
  url: string,
  body?: unknown,
): Promise<T> {
  const res = await fetch(url, {
    method,
    credentials: "include",
    headers: body ? { "content-type": "application/json" } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!res.ok) {
    manejar401(res.status, url);
    let detail = res.statusText;
    try {
      const data = await res.json();
      detail = normalizeDetail(data.detail) ?? detail;
    } catch {
      /* ignore */
    }
    throw new ApiError(res.status, detail);
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/** Sesión expirada en cualquier llamada → al login (audit H26).
 *  Se excluye /api/auth/* para no ciclar el propio login/me. */
function manejar401(status: number, url: string): void {
  if (
    status === 401 &&
    !url.startsWith("/api/auth/") &&
    window.location.pathname !== "/login"
  ) {
    window.location.assign("/login");
  }
}

async function requestForm<T>(url: string, fd: FormData): Promise<T> {
  // No fijamos Content-Type — el browser pone el boundary correcto.
  const res = await fetch(url, {
    method: "POST",
    credentials: "include",
    body: fd,
  });
  if (!res.ok) {
    manejar401(res.status, url);
    let detail = res.statusText;
    try {
      const data = await res.json();
      detail = normalizeDetail(data.detail) ?? detail;
    } catch {
      /* ignore */
    }
    throw new ApiError(res.status, detail);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  get: <T>(url: string) => request<T>("GET", url),
  post: <T>(url: string, body?: unknown) => request<T>("POST", url, body),
  postForm: <T>(url: string, fd: FormData) => requestForm<T>(url, fd),
  put: <T>(url: string, body?: unknown) => request<T>("PUT", url, body),
  patch: <T>(url: string, body?: unknown) => request<T>("PATCH", url, body),
  del: <T>(url: string) => request<T>("DELETE", url),
};

// Generico de paginacion (alineado con backend Page[T])
export type Page<T> = {
  items: T[];
  total: number;
  page: number;
  page_size: number;
};
